import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { Company, Grant, PermissionDefinition } from '../api/business/types';
import { useT } from '../i18n/LanguageContext';

import { hasGrant, withGrant, withoutGrant } from './grantMatrix';

type PermissionMatrixProps = {
  definitions: Array<PermissionDefinition>;
  companies: Array<Company>;
  value: Array<Grant>;
  onChange: (next: Array<Grant>) => void;
  /** Whether the person editing may hand this grant out — they hold it themselves (BR-11). */
  canGrant: (permission: string, companyId: string | null) => boolean;
};

/**
 * Tenant-wide permissions as checkboxes, company permissions as a company × permission grid. Rendered from
 * `GET /permissions`, never from a list written here: a permission added to the API appears on its own
 * (Conventions/09 §4).
 */
export const PermissionMatrix = ({
  definitions,
  companies,
  value,
  onChange,
  canGrant,
}: PermissionMatrixProps) => {
  const t = useT();
  const tenantScoped = definitions.filter((definition) => definition.scope === 'TENANT');
  const companyScoped = definitions.filter((definition) => definition.scope === 'COMPANY');

  const toggle = (permission: string, companyId: string | null, on: boolean): void =>
    onChange(
      on
        ? withGrant(value, definitions, permission, companyId)
        : withoutGrant(value, definitions, permission, companyId),
    );

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="overline" color="text.secondary">
          {t('permissions.tenantWide')}
        </Typography>
        <Stack>
          {tenantScoped.map((definition) => (
            <FormControlLabel
              key={definition.code}
              disabled={!canGrant(definition.code, null)}
              control={
                <Checkbox
                  size="small"
                  checked={hasGrant(value, definition.code, null)}
                  onChange={(event) => toggle(definition.code, null, event.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{definition.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {definition.description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </Stack>
      </Box>

      <Box>
        <Typography variant="overline" color="text.secondary">
          {t('permissions.perCompany')}
        </Typography>
        {companies.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('permissions.noCompanies')}
          </Typography>
        ) : (
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('field.company')}</TableCell>
                  {companyScoped.map((definition) => (
                    <TableCell key={definition.code} align="center">
                      <Tooltip title={definition.description}>
                        <span>{definition.label}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>{company.name}</TableCell>
                    {companyScoped.map((definition) => (
                      <TableCell key={definition.code} align="center" padding="checkbox">
                        <Checkbox
                          size="small"
                          disabled={!canGrant(definition.code, company.id)}
                          checked={hasGrant(value, definition.code, company.id)}
                          onChange={(event) => toggle(definition.code, company.id, event.target.checked)}
                          inputProps={{ 'aria-label': `${company.name} — ${definition.label}` }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Stack>
  );
};
